import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Message, ChatSession } from "../types";
import { useNotification } from "../contexts/NotificationContext";
import { useProject } from "../contexts/ProjectContext";
import { useChatContext } from "../contexts/ChatContext";
import Button from "./Button";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";

const ChatContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: box-shadow ${({ theme }) => theme.transitions.base};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ChatTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  margin-bottom: 1rem;
  scroll-behavior: smooth;
`;

const ChatForm = styled.form`
  display: flex;
  gap: 1rem;
  height: 140px;
  min-height: 140px;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  resize: none;
  font-family: ${({ theme }) => theme.fonts.base};
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 85px;
  flex-direction: column-reverse;
  height: 95%;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: 2rem;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 400px;
  margin: 0;
`;

const ChatArea: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [streamTimeout, setStreamTimeout] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { addNotification } = useNotification();
  const { currentProject } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    messages,
    isProcessing,
    isTyping,
    statusMessages,
    sendMessage,
    resetSession,
    clearMessages,
  } = useChatContext();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Monitor for stream timeout
  useEffect(() => {
    if (isProcessing) {
      const timeoutId = setTimeout(() => {
        setStreamTimeout(true);
        addNotification({
          type: "warning",
          title: "ストリーミング遅延",
          message: "AIの応答に時間がかかっています。しばらくお待ちください。",
        });
      }, 15000); // 15 seconds

      return () => clearTimeout(timeoutId);
    } else {
      setStreamTimeout(false);
    }
  }, [isProcessing, addNotification]);

  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      // 内容が空のAIメッセージは描画しない（タイピングインジケータのみ表示）
      if (message.type === "ai" && !message.content.trim()) return false;
      return true;
    });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Submit button state:", {
      inputValue: inputValue.trim(),
      isProcessing,
      currentProject,
      hasInput: !!inputValue.trim(),
      hasProject: !!currentProject,
    });

    if (!inputValue.trim() || isProcessing || !currentProject) return;

    const userInput = inputValue.trim();
    setInputValue("");

    try {
      const sent = await sendMessage(currentProject.id, userInput, undefined, currentProject.workspace_id);
      if (!sent) {
        // 送れなかった文を入力欄に戻す（別の文を打っていたら上書きしない）
        setInputValue((current) => (current === "" ? userInput : current));
      } else if (searchParams.toString()) {
        // 自動送信に失敗して残したリンクの URL は、手で送れたら消す（あとのリロードで古い内容を送り直さない）
        setSearchParams({}, { replace: true });
      }
    } catch (error) {
      console.error("Send message error:", error);
      addNotification({
        type: "error",
        title: "メッセージ送信エラー",
        message: "メッセージの送信に失敗しました。もう一度お試しください。",
      });
    }
  };

  const handleReset = async () => {
    if (!currentProject) return;

    setInputValue("");
    await resetSession(currentProject.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <ChatTitle>チャット</ChatTitle>
        <Button variant="secondary" onClick={handleReset}>
          + 新規セッション
        </Button>
      </ChatHeader>

      <MessagesContainer>
        {messages.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                  clipRule="evenodd"
                />
              </svg>
            </EmptyStateIcon>
            <EmptyStateTitle>チャットを開始</EmptyStateTitle>
            <EmptyStateDescription>
              {currentProject
                ? "インシデントについて質問してください。AIが分析結果とレポートを生成します。"
                : "プロジェクトを選択してからチャットを開始してください。"}
            </EmptyStateDescription>
          </EmptyState>
        ) : (
          <>
            {filteredMessages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isTyping && (
              <TypingIndicator
                statusMessages={statusMessages}
                showIndicator={true}
              />
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      <ChatForm onSubmit={handleSubmit}>
        <MessageInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          placeholder="メッセージを入力..."
          disabled={isProcessing}
        />
        <ButtonContainer>
          <Button
            type="submit"
            disabled={!inputValue.trim() || isProcessing || !currentProject}
            loading={isProcessing}
            onClick={() => {
              console.log("Button clicked - state check:", {
                inputEmpty: !inputValue.trim(),
                isProcessing,
                noProject: !currentProject,
                disabled: !inputValue.trim() || isProcessing || !currentProject,
              });
            }}
          >
            送信
          </Button>
        </ButtonContainer>
      </ChatForm>
    </ChatContainer>
  );
};

export default ChatArea;
